/**
 * Glossary engine (W2-22, 2026-06-11) — pure, React-free lookup + matcher.
 *
 * WHY a separate module from glossary-data.ts: the DATA is authored content
 * (its own authoring contract); the ENGINE is mechanics. The
 * matcher, the chat post-processor, and the completeness lint all import from
 * here without pulling in React — vitest runs it under plain node.
 *
 * Matcher design (the fiddly part — see the unit tests for the contract):
 * - ONE pass, leftmost-longest, non-overlapping. Two compiled alternation
 *   regexes (case-sensitive + case-insensitive) merged by (start asc, len desc).
 * - Word boundaries are checked in JS, not via `\b` — `\b` fails on terms
 *   containing §, /, digits, parens ("§179", "A/R", "401(k)", "W-2").
 * - CASE RULES (operator-locked, per-STRING): for entries flagged
 *   `caseSensitive`, any matchable string that is short-all-caps (≤6 chars,
 *   no lowercase, no spaces — may contain §/digits/slash like "A/R") matches
 *   CASE-SENSITIVELY ("fire" ≠ FIRE, "Salt" ≠ SALT). Their longer word-phrase
 *   aliases ("Adjusted Gross Income") match case-insensitively, as does every
 *   string of non-caseSensitive entries.
 * - Plural: word-phrase strings (ending in a lowercase letter) also match
 *   with a trailing "s" ("carryforwards", "safe harbors"). All-caps strings
 *   never do ("QBIs" is NOT a QBI match — boundary check rejects it).
 * - Backtick code spans (`like this`) are never matched inside.
 */

import { GLOSSARY, type GlossaryEntry } from './glossary-data';

export type { GlossaryEntry };

const BY_KEY: Map<string, GlossaryEntry> = new Map(GLOSSARY.map((e) => [e.key, e]));

/** Strict lookup by canonical glossary key. */
export function getEntry(key: string): GlossaryEntry | undefined {
  return BY_KEY.get(key);
}

/**
 * Every key the legacy InfoTooltip TOOLTIP_DATABASE ever shipped → its
 * glossary key — INCLUDING the two BROKEN production keys (`qbi` and
 * `apportionment`, TaxPlanning.tsx:312/534) that silently rendered nothing
 * for months because the old component fell back to bare children on a miss.
 * The completeness lint walks src/ and fails on any `term=` that doesn't
 * resolve through this map, so that bug class is now impossible.
 */
export const LEGACY_KEY_MAP: Record<string, string> = {
  'section-105': 'section-105',
  'solo-401k': 'solo-401k',
  's-corp': 's-corp',
  'reasonable-compensation': 'reasonable-compensation',
  'safe-harbor': 'safe-harbor',
  'irc-162': 'irc-162',
  'section-199a': 'qbi',
  'salt-cap': 'salt',
  'se-tax': 'se-tax',
  '1099-nec': '1099-nec',
  'mileage-rate': 'mileage-rate',
  // The two keys that were referenced in production but never existed in the
  // old TOOLTIP_DATABASE — they now resolve to real entries (Landmine A fix).
  qbi: 'qbi',
  apportionment: 'apportionment',
};

/** Resolve a legacy InfoTooltip `term` OR a canonical key to its entry. */
export function resolveEntry(termOrKey: string): GlossaryEntry | undefined {
  return getEntry(LEGACY_KEY_MAP[termOrKey] ?? termOrKey);
}

export interface GlossaryMatch {
  /** inclusive start index in the input string */
  start: number;
  /** exclusive end index */
  end: number;
  /** glossary entry key */
  key: string;
  /** the exact text that matched (may include a plural "s") */
  matched: string;
}

// ---------------------------------------------------------------------------
// matcher internals
// ---------------------------------------------------------------------------

/**
 * Per the operator-locked caseSensitive semantics: a string is "short-all-caps"
 * when it's ≤6 chars, contains no lowercase letters and no spaces. Covers QBI,
 * AMT, NIIT, SALT, FICA, EBITDA, A/R, A/P, §1411-style refs — but NOT the
 * spelled-out aliases ("Net Investment Income Tax") or "NJ BAIT" (7 chars).
 */
function isShortAllCaps(s: string): boolean {
  return s.length <= 6 && !/[a-z]/.test(s) && !/\s/.test(s);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface Compiled {
  /** exact-string → key for case-sensitive candidates */
  csMap: Map<string, string>;
  /** lowercased-string → key for case-insensitive candidates */
  ciMap: Map<string, string>;
  csRe: RegExp | null;
  ciRe: RegExp | null;
}

let _compiled: Compiled | null = null;

function compile(): Compiled {
  interface MatchCandidate {
    canonical: string;
    key: string;
    cs: boolean;
    plural: boolean;
  }
  const candidates: MatchCandidate[] = [];
  for (const e of GLOSSARY) {
    for (const s of [e.term, ...(e.aliases ?? [])]) {
      const cs = !!e.caseSensitive && isShortAllCaps(s);
      candidates.push({
        canonical: s,
        key: e.key,
        cs,
        // plural "s" only for word phrases (last char lowercase) — never for
        // all-caps abbreviations, so "QBIs" stays unmatched.
        plural: !cs && /[a-z]$/.test(s),
      });
    }
  }
  const cs = candidates.filter((c) => c.cs).sort((a, b) => b.canonical.length - a.canonical.length);
  const ci = candidates.filter((c) => !c.cs).sort((a, b) => b.canonical.length - a.canonical.length);
  // Alternation order = longest first → JS regex picks the longest alternative
  // at the leftmost position ("bonus depreciation" beats "depreciation").
  const csRe = cs.length
    ? new RegExp(cs.map((c) => escapeRegex(c.canonical)).join('|'), 'g')
    : null;
  const ciRe = ci.length
    ? new RegExp(ci.map((c) => escapeRegex(c.canonical) + (c.plural ? 's?' : '')).join('|'), 'gi')
    : null;
  return {
    csMap: new Map(cs.map((c) => [c.canonical, c.key])),
    ciMap: new Map(ci.map((c) => [c.canonical.toLowerCase(), c.key])),
    csRe,
    ciRe,
  };
}

function getCompiled(): Compiled {
  if (!_compiled) _compiled = compile();
  return _compiled;
}

const WORD_CHAR = /[A-Za-z0-9]/;

/**
 * JS-side word boundary: the char before must not be alphanumeric; the char
 * after must not be alphanumeric AND must not be a hyphen glued to more word
 * chars (rejects "W-2" inside "W-22" and "Form 1120" inside "Form 1120-X").
 */
function boundaryOk(text: string, start: number, end: number): boolean {
  if (start > 0 && WORD_CHAR.test(text[start - 1])) return false;
  const after = text[end];
  if (after !== undefined) {
    if (WORD_CHAR.test(after)) return false;
    if (after === '-' && end + 1 < text.length && WORD_CHAR.test(text[end + 1])) return false;
  }
  return true;
}

/** Paired-backtick code spans — matches inside them are skipped entirely. */
function codeSpanRanges(text: string): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  let i = 0;
  for (;;) {
    const a = text.indexOf('`', i);
    if (a === -1) break;
    const b = text.indexOf('`', a + 1);
    if (b === -1) break; // unpaired trailing backtick — ignore
    ranges.push([a, b + 1]);
    i = b + 1;
  }
  return ranges;
}

function overlapsAny(start: number, end: number, ranges: Array<[number, number]>): boolean {
  return ranges.some(([a, b]) => start < b && end > a);
}

/**
 * Find every glossary term in a plain string. Longest-match-first,
 * word-boundary-safe, non-overlapping, case rules per entry, skips backtick
 * code spans. Pure — safe to call from render (memoize) or from node tests.
 */
export function matchGlossaryTerms(text: string): GlossaryMatch[] {
  if (!text) return [];
  const { csMap, ciMap, csRe, ciRe } = getCompiled();
  const excluded = codeSpanRanges(text);
  const raw: GlossaryMatch[] = [];

  const collect = (re: RegExp | null, caseSensitive: boolean) => {
    if (!re) return;
    re.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const start = m.index;
      const matched = m[0];
      const end = start + matched.length;
      if (matched.length === 0) {
        re.lastIndex = start + 1; // defensive — no empty candidates exist
        continue;
      }
      if (!boundaryOk(text, start, end) || overlapsAny(start, end, excluded)) {
        re.lastIndex = start + 1; // retry from the next char, not past the match
        continue;
      }
      let key: string | undefined;
      if (caseSensitive) {
        key = csMap.get(matched);
      } else {
        const lower = matched.toLowerCase();
        key = ciMap.get(lower);
        // plural form matched — strip the trailing "s" for the lookup
        if (!key && lower.endsWith('s')) key = ciMap.get(lower.slice(0, -1));
      }
      if (!key) {
        re.lastIndex = start + 1;
        continue;
      }
      raw.push({ start, end, key, matched });
    }
  };

  collect(csRe, true);
  collect(ciRe, false);

  // Merge the two scans: leftmost wins; at the same start, longest wins.
  raw.sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
  const out: GlossaryMatch[] = [];
  let lastEnd = -1;
  for (const m of raw) {
    if (m.start >= lastEnd) {
      out.push(m);
      lastEnd = m.end;
    }
  }
  return out;
}
